/**
 * The contexts this app hands <AdminAppLayout>, and the pages inside them.
 *
 * A CONTEXT is the level above Sections and Pages — what the shell's switcher
 * switches between. The design calls it an "App"; here it is a context (ruled
 * 2026-09-22), because "app" is the webapp itself. Two to start:
 *
 *   Platform Administration — platform-level: it administers every house, so
 *   the shell shows no house switcher. The five administration collections.
 *
 *   Auction Operations — house-level: the person stands inside one house, and
 *   the shell's house switcher says which. Operate holds the one page that is
 *   real today, Live Sales, which is where the clerk console opens from.
 *   Configure and Reconcile are present in the design's shape, marked
 *   "Coming Soon", each page routed to one placeholder view — a deliberate
 *   exception to the no-dead-ends rule below, so the shape is shown and the
 *   mark says why nothing is behind it yet.
 *
 * What did not come across from vibe-portal's platformNav.js, and why:
 * persona gating (authorization, which is not in this Effort), its nine
 * unmarked placeholder pages (a menu of dead ends that teaches people not to
 * trust it — the marked ones above are the exception, not a return), and
 * `section.color`, which nothing ever read.
 *
 * Every page names its `view` — which screen renders it — and its `path`, so
 * the router is built from this list rather than written a second time. A
 * collection page carries its `singular` because a new record is named after
 * one, and dropping a trailing `s` from the label produces "New Companie".
 */

export const PLATFORM_ADMINISTRATION = {
    id: 'platform-administration',
    name: 'Platform Administration',
    description: 'Companies, operations, houses and dealerships',
    icon: 'shield',
    // Read by the shell to hide the auction-house switcher.
    platformLevel: true,
    sections: [
        {
            id: 'companies',
            label: 'Companies',
            description: 'The legal entities everything else names',
            pages: [
                {
                    id: 'companies',
                    label: 'Companies',
                    singular: 'Company',
                    icon: 'briefcase',
                    routeName: 'companies',
                    path: '/companies',
                    view: 'collection'
                }
            ]
        },
        {
            id: 'operations',
            label: 'Operations',
            description: 'The brands that run auctions and dealer groups',
            pages: [
                {
                    id: 'auction-operations',
                    label: 'Auction Operations',
                    singular: 'Auction Operation',
                    icon: 'sitemap',
                    routeName: 'auction-operations',
                    path: '/auction-operations',
                    view: 'collection'
                },
                {
                    id: 'dealership-operations',
                    label: 'Dealership Operations',
                    singular: 'Dealership Operation',
                    icon: 'sitemap',
                    routeName: 'dealership-operations',
                    path: '/dealership-operations',
                    view: 'collection'
                }
            ]
        },
        {
            id: 'places',
            label: 'Places',
            description: 'Where sales run, and who buys at them',
            pages: [
                {
                    id: 'auction-houses',
                    label: 'Auction Houses',
                    singular: 'Auction House',
                    icon: 'building',
                    routeName: 'auction-houses',
                    path: '/auction-houses',
                    view: 'collection'
                },
                {
                    id: 'dealerships',
                    label: 'Dealerships',
                    singular: 'Dealership',
                    icon: 'car',
                    routeName: 'dealerships',
                    path: '/dealerships',
                    view: 'collection'
                }
            ]
        },
        // Identity's two records, read and written on the identity server rather than the
        // administration server (the identity ADR, point 10), so each has a view of its own instead
        // of the five nouns' shared one.
        {
            id: 'identity',
            label: 'Identity',
            description: 'The people and the programs that sign in, and their sessions',
            pages: [
                {
                    id: 'humans',
                    label: 'Humans',
                    singular: 'Human',
                    icon: 'users',
                    routeName: 'humans',
                    path: '/humans',
                    view: 'humans'
                },
                {
                    id: 'machines',
                    label: 'Machines',
                    singular: 'Machine',
                    icon: 'server',
                    routeName: 'machines',
                    path: '/machines',
                    view: 'machines'
                }
            ]
        }
    ]
}

// One placeholder view, many pages. The blurbs are the design's own copy,
// with its retired words converted (an asset is never a lot, and never a
// vehicle as the generic).
function comingSoon({ id, label, icon, section, blurb }) {
    return {
        id,
        label,
        icon,
        routeName: id,
        path: `/${section}/${id}`,
        view: 'coming-soon',
        blurb
    }
}

export const AUCTION_OPERATIONS = {
    id: 'auction-operations-context',
    name: 'Auction Operations',
    description: 'Run the sales a house holds',
    icon: 'wrench',
    platformLevel: false,
    // Where switching into this context lands. Configure sits first in the nav
    // (ruled 2026-09-22) but is Coming Soon, so the home is named, not derived.
    home: 'live-sales',
    sections: [
        {
            id: 'configure',
            label: 'Configure (Coming Soon)',
            description: 'Pre-sale setup and rules',
            pages: [
                comingSoon({
                    id: 'sale-definitions',
                    label: 'Sale Definitions',
                    icon: 'flag',
                    section: 'configure',
                    blurb: 'The recurring sales a house holds: their names, their cadence, their lanes, and the rules each one runs under.'
                }),
                comingSoon({
                    id: 'bidding-rules-templates',
                    label: 'Bidding Rules Templates',
                    icon: 'bolt',
                    section: 'configure',
                    blurb: 'Reusable sets of bidding rules, such as increments, reserves and extensions, that you define once and apply across sales.'
                }),
                comingSoon({
                    id: 'lane-definitions',
                    label: 'Lane Definitions',
                    icon: 'warehouse',
                    section: 'configure',
                    blurb: 'The lanes a house runs, physical and digital, and what each one carries.'
                }),
                comingSoon({
                    id: 'inventory-sources',
                    label: 'Inventory Sources',
                    icon: 'database',
                    section: 'configure',
                    blurb: 'The feeds and integrations that bring assets into your sales, with mapping and sync status for each source.'
                }),
                comingSoon({
                    id: 'configure-settings',
                    label: 'Settings',
                    icon: 'cog',
                    section: 'configure',
                    blurb: 'How this house plans its sales.'
                })
            ]
        },
        {
            id: 'operate',
            label: 'Operate',
            description: 'Live operations and monitoring',
            pages: [
                {
                    id: 'live-sales',
                    label: 'Live Sales',
                    icon: 'circle-fill',
                    routeName: 'live-sales',
                    path: '/operate/live-sales',
                    view: 'live-sales'
                }
            ]
        },
        {
            id: 'reconcile',
            label: 'Reconcile (Coming Soon)',
            description: 'Review results and financials',
            pages: [
                comingSoon({
                    id: 'offers',
                    label: 'Offers',
                    icon: 'check-circle',
                    section: 'reconcile',
                    blurb: 'Review and action post-sale offers on no-sale and below-reserve assets: negotiate, accept, or counter to close the gap between buyers and sellers.'
                }),
                comingSoon({
                    id: 'sales-recap',
                    label: 'Sales Recap',
                    icon: 'chart-bar',
                    section: 'reconcile',
                    blurb: 'Post-sale results, such as sell-through, gross and per-asset outcomes, plus the financial summaries for settlement.'
                })
            ]
        }
    ]
}

/** Every context the switcher offers, in the order it lists them. */
export const CONTEXTS = [PLATFORM_ADMINISTRATION, AUCTION_OPERATIONS]

/** Every page, flattened and stamped with its context — what the router registers and the shell resolves against. */
export const PAGES = CONTEXTS.flatMap((context) =>
    context.sections.flatMap((section) => section.pages.map((page) => ({ ...page, contextId: context.id })))
)

/** The page a route name belongs to, so a host can derive `activePage`. */
export function findByRouteName(routeName) {
    return PAGES.find((page) => page.routeName === routeName) ?? null
}

/** The page a nav id names, so a host can turn a `navigate` intent into a route. */
export function findById(pageId) {
    return PAGES.find((page) => page.id === pageId) ?? null
}

/** The context a page belongs to, so a host can hand the shell the right tree. */
export function contextOf(pageId) {
    const page = findById(pageId)
    if (page === null) {
        return null
    }
    // Every page came out of a context, so this always finds one.
    return CONTEXTS.find((context) => context.id === page.contextId)
}

/** Where a context opens: its first section's first page. */
export function homeOf(context) {
    if (typeof context.home === 'string') {
        for (const section of context.sections) {
            const page = section.pages.find((each) => each.id === context.home)
            if (page !== undefined) {
                return page
            }
        }
    }
    return context.sections[0].pages[0]
}
